import type * as Flows from './types.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import { getAllElements } from './element.no-deps'
import { dateToAnswerValue } from './dateTime.no-deps'
import type { CreateAnswerBase } from './answer.no-deps'
import { getFlowUnitIds } from './FlowUnitsIds.no-deps'
import type { TFullUnit } from '../units/types.no-deps'
import { getUnitIdsFromLoopPlan, type LoopPlan } from './LoopPlan.no-deps'
import type { LoopPathIds } from './LoopPath.no-deps'
import type { WorkflowReference } from '../../../shared/domain/workflows/types.no-deps'
import { getVersionIdentifier } from '../workflows/types.no-deps'

interface GenerateDefaultAnswersArgs {
    flow: Flows.Flow
    reportStartDate: Date
    createAnswerBase: CreateAnswerBase
    allUnits: TFullUnit[]
    existingAnswers?: Flows.Answer[]
    loopPlan: LoopPlan
    guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>
    subjectUnitId: number | null
}

export function generateDefaultAnswers(args: GenerateDefaultAnswersArgs): Flows.Answer[] {
    function recurse(section: Flows.HasSteps, loopPathIds: LoopPathIds) {
        const defaultAnswers: Flows.Answer[] = []
        const existingAnswers = args.existingAnswers ?? []
        const existingAnswerElementIds = new Set(existingAnswers.map((answer) => answer.element))
        const elements = getAllElements(section, { traverseLoops: false })
        const flowUnitIds = getFlowUnitIds(args.allUnits, section)

        const createDefaultAnswerBase: CreateAnswerBase = (element, loopPathIds) => ({
            ...args.createAnswerBase(element, loopPathIds),
            timestamp: args.reportStartDate,
            automaticallyGenerated: true,
        })

        // If an existing answer for the dynamic guide element has an empty value, generate a new default answer for this element, as the existing answer might have been created before fetching dynamic guides.
        const emptyDynamicGuideAnswerValue = (element: Flows.Element) => {
            const existingAnswer = existingAnswers.find((answer) => answer.element === element.id)
            if (!existingAnswer) return false
            return (
                existingAnswer.type === 'guide' &&
                existingAnswer.dynamicElement &&
                element.type === 'guide' &&
                element.dynamicGuideSettings &&
                Array.isArray(existingAnswer.value) &&
                existingAnswer.value.length === 0
            )
        }

        for (const element of elements) {
            if (element.id && existingAnswerElementIds.has(element.id) && !emptyDynamicGuideAnswerValue(element)) {
                continue
            }
            if (element.type === 'datetime') {
                defaultAnswers.push({
                    ...createDefaultAnswerBase(element.id, loopPathIds),
                    type: 'datetime',
                    value: dateToAnswerValue(args.reportStartDate, element.selectType),
                })
            }

            if (element.type === 'unit-select') {
                const unitIds = flowUnitIds[element.id]
                if (unitIds.length > 0) continue
                defaultAnswers.push({
                    ...createDefaultAnswerBase(element.id, loopPathIds),
                    type: 'unit-select',
                    value: 'no-units-available',
                })
            }

            if (element.type === 'loop') {
                const loopBreadthIds = getUnitIdsFromLoopPlan(args.loopPlan, element.id)
                for (const loopId of loopBreadthIds) {
                    defaultAnswers.push(...recurse(element, [...loopPathIds, loopId]))
                }
            }

            if (element.type === 'guide' && element.dynamicGuideSettings) {
                let dynamicGuides: Array<{ guide: Guide.SyncGuide; workflow: WorkflowReference }> = Array.from(
                    args.guideElementsContextMap.values()
                ).filter(({ guide }) =>
                    guide.tags
                        .filter((tag: Guide.GuideTag) => tag.id)
                        .map((guideTag: Guide.GuideTag) => guideTag.id)
                        .includes(element.dynamicGuideSettings.tagId)
                )

                if (args.subjectUnitId) {
                    // Only keep dynamic guides that are associated with the subject unit
                    dynamicGuides = dynamicGuides.filter(({ guide }) => guide.units.includes(args.subjectUnitId))
                }

                const dynamicGuideAnswers = dynamicGuides.map((dynamicGuide) => {
                    const versionIdentifier = dynamicGuide.workflow ? getVersionIdentifier(dynamicGuide.workflow) : null

                    return {
                        guideId: dynamicGuide.guide.id,
                        type: dynamicGuide.guide.variant,
                        completed: false,
                        ...(versionIdentifier && { version: versionIdentifier.version }),
                    }
                })

                defaultAnswers.push({
                    ...createDefaultAnswerBase(element.id, loopPathIds),
                    type: 'guide',
                    dynamicElement: true,
                    value: dynamicGuideAnswers,
                })
            }

            if (element.type === 'guide' && !element.dynamicGuideSettings) {
                const guideElementContext = args.guideElementsContextMap.get(element.guideId)
                if (!guideElementContext) continue // Don't try to add a default answer if a guide element context wasn't provided.

                const { guide, workflow } = guideElementContext
                const versionIdentifier = workflow ? getVersionIdentifier(workflow) : null

                defaultAnswers.push({
                    ...createDefaultAnswerBase(element.id, loopPathIds),
                    type: 'guide',
                    value: [
                        {
                            guideId: element.guideId,
                            type: guide.variant,
                            completed: false,
                            ...(versionIdentifier && { version: versionIdentifier.version }),
                        },
                    ],
                })
            }
        }

        return defaultAnswers
    }

    return recurse(args.flow, [])
}
